import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronRight, Receipt, CalendarClock, Bell } from 'lucide-react';
import { ADMIN_NAV_GROUPS, IconType } from '../../../config/adminNav';
import { PendingItem, PendingSeverity } from './types';

// Destino de cada pendência; o ícone vem do item do menu (navKey) para não duplicar config
const PENDING_TARGETS: Record<string, { href: string; navKey?: string; icon?: IconType }> = {
  paymentsOverdue: { href: '/admin/crm/pagamentos', icon: Receipt },
  paymentsDueSoon: { href: '/admin/crm/pagamentos', icon: Receipt },
  visaAnalysis: { href: '/admin/analise-visto', navKey: 'visaAnalysis' },
  residenceAnalysis: { href: '/admin/analise-residencia', navKey: 'residenceAnalysis' },
  nif: { href: '/admin/nif', navKey: 'nif' },
  contacts: { href: '/admin/contacts', navKey: 'contacts' },
  bookings: { href: '/admin/agendamentos?tab=reservas', icon: CalendarClock },
  contracts: { href: '/admin/contrato', navKey: 'contract' },
  quotes: { href: '/admin/orcamento', navKey: 'orcamento' },
  testimonials: { href: '/admin/testimonials', navKey: 'testimonials' },
};

const NAV_ICONS: Record<string, IconType> = Object.fromEntries(
  ADMIN_NAV_GROUPS.flatMap((group) => group.items.map((item) => [item.key, item.icon]))
);

const SEVERITY_STYLES: Record<PendingSeverity, { row: string; icon: string; badge: string }> = {
  urgent: { row: 'border-red-200 bg-red-50 hover:bg-red-100', icon: 'bg-red-500', badge: 'bg-red-600 text-white' },
  high: { row: 'border-orange-200 bg-orange-50 hover:bg-orange-100', icon: 'bg-orange-500', badge: 'bg-orange-500 text-white' },
  medium: { row: 'border-amber-200 bg-amber-50 hover:bg-amber-100', icon: 'bg-amber-500', badge: 'bg-amber-500 text-white' },
  low: { row: 'border-gray-200 bg-white hover:bg-gray-50', icon: 'bg-gray-500', badge: 'bg-gray-200 text-gray-800' },
  info: { row: 'border-blue-200 bg-blue-50 hover:bg-blue-100', icon: 'bg-blue-500', badge: 'bg-blue-500 text-white' },
};

interface PendingPanelProps {
  items: PendingItem[];
}

const PendingPanel: React.FC<PendingPanelProps> = ({ items }) => {
  const { t } = useTranslation();
  const visible = items.filter((item) => item.count > 0 && PENDING_TARGETS[item.key]);
  const total = visible.reduce((acc, item) => acc + item.count, 0);

  return (
    <div className="card h-full">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
          {t('dashboardHome.pending.title')}
        </h2>
        {total > 0 && (
          <span className="px-2.5 py-0.5 rounded-full bg-red-600 text-white text-sm font-semibold tabular-nums">{total}</span>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{t('dashboardHome.pending.allClear')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {visible.map((item) => {
            const target = PENDING_TARGETS[item.key];
            const Icon = target.icon || (target.navKey && NAV_ICONS[target.navKey]) || Bell;
            const styles = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.low;
            return (
              <Link
                key={item.key}
                to={target.href}
                className={`flex items-center gap-3 p-3 sm:p-4 border rounded-lg transition-colors duration-150 group ${styles.row}`}
              >
                <div className={`w-9 h-9 ${styles.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="flex-1 min-w-0 text-sm font-medium text-gray-900">
                  {t(`dashboardHome.pending.${item.key}`)}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-sm font-semibold tabular-nums ${styles.badge}`}>
                  {item.count}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PendingPanel;
